import { useEffect, useRef, useState } from 'react';
import type { GoogleDriveQuotaSettingsView } from '@tosho/contracts';
import { googleDriveApi, GoogleDriveApiError } from './google-drive-api';
import { quotaDisplay, quotaUnits } from './drive-quota-units';

/** Aucun seuil proposé à la place de l'Administrateur. Une lecture échouée n'est jamais un
 * réglage absent ; un conflit exige une relecture explicite, sans écraser l'autre auteur. */
export function DriveQuotaSettingsCard({
  csrfToken,
  enabled,
  onReauthenticate,
}: {
  readonly csrfToken: string;
  readonly enabled: boolean;
  readonly onReauthenticate: () => void;
}): React.JSX.Element {
  const [view, setView] = useState<GoogleDriveQuotaSettingsView | null>(null);
  const [known, setKnown] = useState(false),
    [busy, setBusy] = useState(false);
  const [reserve, setReserve] = useState(''),
    [maximum, setMaximum] = useState(''),
    [age, setAge] = useState('');
  const [error, setError] = useState<string | null>(null),
    [saved, setSaved] = useState(false),
    [reauth, setReauth] = useState(false);
  const generation = useRef(0),
    running = useRef(false);
  async function load(): Promise<void> {
    const request = ++generation.current;
    setKnown(false);
    setError(null);
    setSaved(false);
    try {
      const result = await googleDriveApi.readQuotaSettings();
      if (request !== generation.current) return;
      setView(result);
      setReserve(result ? quotaDisplay(result.reserveBytes, 9) : '');
      setMaximum(result ? quotaDisplay(result.maximumFileBytes, 6) : '');
      setAge(result ? quotaDisplay(String(result.maxObservationAgeMs), 3) : '');
      setKnown(true);
    } catch {
      if (request === generation.current)
        setError('Les réglages ne peuvent pas être chargés. Réessayez.');
    }
  }
  useEffect(() => {
    if (enabled) void load();
    else {
      generation.current++;
      setKnown(false);
      setView(null);
      setSaved(false);
    }
    return () => {
      generation.current++;
    };
  }, [enabled, csrfToken]);
  async function save(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!known || !enabled || running.current) return;
    setSaved(false);
    setError(null);
    setReauth(false);
    let settings;
    try {
      const ageMs = BigInt(quotaUnits(age, 3));
      if (ageMs <= 0n || ageMs > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error();
      settings = {
        reserveBytes: quotaUnits(reserve, 9),
        maximumFileBytes: quotaUnits(maximum, 6),
        maxObservationAgeMs: Number(ageMs),
      };
      if (
        BigInt(settings.maximumFileBytes) <= 0n ||
        BigInt(settings.maximumFileBytes) > 9223372036854775807n ||
        BigInt(settings.reserveBytes) > 9223372036854775807n
      )
        throw new Error();
    } catch {
      setError(
        'Renseignez les trois valeurs : réserve positive ou nulle, taille et durée strictement positives.',
      );
      return;
    }
    const request = generation.current;
    running.current = true;
    setBusy(true);
    try {
      const result = await googleDriveApi.saveQuotaSettings(csrfToken, {
        settings,
        expectedRevision: view?.revision ?? null,
      });
      if (request !== generation.current) return;
      setView(result);
      setSaved(true);
    } catch (cause) {
      if (request !== generation.current) return;
      if (cause instanceof GoogleDriveApiError && cause.code === 'DRIVE_QUOTA_SETTINGS_CONFLICT') {
        setKnown(false);
        setError(
          'Ces réglages ont été modifiés ailleurs. Rechargez-les avant de réessayer. Vos saisies seront remplacées.',
        );
      } else if (
        cause instanceof GoogleDriveApiError &&
        cause.code === 'REAUTHENTICATION_REQUIRED'
      ) {
        setReauth(true);
        setError('Confirmez votre identité pour enregistrer les réglages.');
      } else setError('Les réglages n’ont pas pu être enregistrés. Réessayez.');
    } finally {
      running.current = false;
      setBusy(false);
    }
  }
  return (
    <section
      className="drive-integration__card drive-integration__quota-card"
      id="drive-quota-settings"
      aria-labelledby="drive-quota-settings-title"
    >
      <header>
        <p className="dashboard-panel__eyebrow">Protection de l’espace</p>
        <h2 id="drive-quota-settings-title">Limites des futurs dépôts</h2>
      </header>
      <p>
        Choisissez l’espace à garder libre, la taille maximale d’un fichier et l’ancienneté maximale
        d’une mesure de capacité. Aucun seuil n’est prérempli.
      </p>
      {!enabled ? (
        <p>Une connexion Drive active est nécessaire.</p>
      ) : (
        <>
          {!known && !error && <p role="status">Chargement des réglages…</p>}
          {known && !view && <p>Aucune limite enregistrée. Les dépôts restent indisponibles.</p>}
          <form
            className="drive-integration__quota-settings"
            onSubmit={(event) => void save(event)}
          >
            <label>
              Espace à garder libre (Go)
              <input
                inputMode="decimal"
                maxLength={40}
                value={reserve}
                onChange={(e) => {
                  setReserve(e.target.value);
                  setSaved(false);
                }}
                disabled={!known || busy}
                required
              />
            </label>
            <label>
              Taille maximale d’un fichier (Mo)
              <input
                inputMode="decimal"
                maxLength={40}
                value={maximum}
                onChange={(e) => {
                  setMaximum(e.target.value);
                  setSaved(false);
                }}
                disabled={!known || busy}
                required
              />
            </label>
            <label>
              Ancienneté maximale d’une mesure (secondes)
              <input
                inputMode="decimal"
                maxLength={40}
                value={age}
                onChange={(e) => {
                  setAge(e.target.value);
                  setSaved(false);
                }}
                disabled={!known || busy}
                required
              />
            </label>
            <p>
              1 Go = 1 000 Mo. L’espace Google est partagé avec ses autres services. Enregistrer ces
              limites n’active pas encore les dépôts.
            </p>
            <button type="submit" disabled={!known || busy}>
              {busy ? 'Enregistrement…' : 'Enregistrer les limites'}
            </button>
          </form>
          {!known && error && (
            <button type="button" onClick={() => void load()}>
              Recharger les réglages
            </button>
          )}
          {saved && (
            <p role="status" className="tosho-feedback is-success">
              Limites enregistrées.
            </p>
          )}
          {reauth && (
            <button type="button" onClick={onReauthenticate}>
              Confirmer mon identité
            </button>
          )}
        </>
      )}
      {error && (
        <p className="tosho-feedback is-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
